import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"

interface DatePickerProps {
    date?: Date
    setDate: (date?: Date) => void
    className?: string
    placeholder?: string
    fromYear?: number
    toYear?: number
    defaultMonth?: Date
    /**
     * TWO NAMED PROPS, NOT A `buttonProps` SPREAD. Only these two are needed to make the trigger
     * a real labelled, described control — `id` so a caller's `<Label htmlFor>` finally points at
     * something, `aria-describedby` so a warning beside the field is read with it — and naming
     * them keeps the prop list closed and typed. A spread would hand every caller the whole
     * `Button` surface (`onClick`, `type`, `disabled`) through a slot this component does not
     * own: `PopoverTrigger asChild` already owns the trigger's behaviour.
     */
    id?: string
    'aria-describedby'?: string
}

export function DatePicker({
    date, setDate, className, placeholder = "Pick a date",
    fromYear = 1950, toYear = new Date().getFullYear() + 5,
    defaultMonth, id, 'aria-describedby': describedBy,
}: DatePickerProps) {

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    id={id}
                    aria-describedby={describedBy}
                    variant={"outline"}
                    className={cn(
                        "w-full justify-start text-left font-normal h-12",
                        !date && "text-muted-foreground",
                        className
                    )}
                >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : <span>{placeholder}</span>}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                    captionLayout="dropdown-buttons"
                    fromYear={fromYear}
                    toYear={toYear}
                    defaultMonth={date || defaultMonth || new Date()}
                />
            </PopoverContent>
        </Popover>
    )
}
